import { useCallback, useEffect, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { adminApi, type ActivityStorage } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { bytes, count, duration, numeric } from '../lib/format.js';
import {
  AsyncBoundary,
  Badge,
  DotPill,
  EmptyState,
  Note,
  Num,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { InfoBubble } from '../components/Screen.js';
import { TabNav, resolveTab } from '../components/Record.js';
import { HealthState } from './ApplicationDetail.js';

/**
 * System Health, in two sections.
 *
 * ── Uptime, and the activity log's partitioning trigger ─────────────
 *
 * They are here together because they answer the same question from opposite
 * ends: is the platform all right. One is about the services being reachable,
 * the other about the one table whose size is driven by usage rather than by
 * how many accounts exist.
 *
 * ── The activity half is a TRIGGER, not a dashboard (org-admin#16) ───
 *
 * RidgeWill/auth#51 defers partitioning `activity_events` by month until one of
 * two things is true: the table passes a few million rows, or the daily purge
 * takes more than a few seconds. That deferral is correct. Partitions have to be
 * created ahead of time, which is a real moving part, and there are a few
 * thousand rows in the table today.
 *
 * The risk is not that the decision was deferred. It is that a deferred decision
 * with nothing measuring its trigger becomes a permanent one silently, and by
 * the time anybody notices, the cheap moment to have acted is years past.
 *
 * ── The verdict is loud only when it is interesting (org-admin#56) ───
 *
 * That half first shipped with the verdict as a standing paragraph at the top,
 * in all three states. A block of text present on every single visit is read
 * twice and skipped forever after, so the page spent its most prominent space
 * on something nobody was taking in — and that is exactly the space needed on
 * the visit where a threshold HAS been crossed.
 *
 * So the healthy answer is a pill in the panel header with the reasoning behind
 * an information bubble, and the top is empty until there is something to put
 * there. Warning, not danger, when it fires: crossing a threshold breaks
 * nothing, and a screen that paints a capacity signal red is one people stop
 * believing.
 *
 * ── What is NOT here ────────────────────────────────────────────────
 *
 * Ninety days of uptime history, which org-admin#57 asked for and which has no
 * data source: nothing probes these services on a schedule, so there is no
 * history to draw. Deliberately left out rather than faked from the handful of
 * manual runs on record.
 *
 * p95 on `/token` and `/authorize`, and Prisma pool saturation, are
 * org-admin#53 — both need a metrics source that does not exist, and neither
 * can be answered from the auth database.
 */
const SECTIONS = [
  { path: 'uptime', label: 'Uptime' },
  { path: 'activity', label: 'Activity log' },
] as const;

type Section = (typeof SECTIONS)[number]['path'];

/**
 * Anything unrecognised falls back to the first section rather than rendering
 * nothing. A mistyped link, or a bookmark from before this split existed,
 * should land somewhere useful rather than on a blank page.
 *
 * Exported for its test. The fallback is the whole behaviour and it has no
 * visible failure mode — a bad segment would simply render nothing, which looks
 * like a loading state that never finishes.
 */
export function resolve(section?: string): Section {
  return resolveTab(SECTIONS, section);
}

export function SystemHealth({ section }: { section?: string }) {
  const current = resolve(section);

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/*
        The SAME tab strip the record screens use, rather than a second one that
        looks like it. This screen invented the pattern — a section as a path
        segment, so an incident link can point at "uptime" rather than at "the
        health screen, then click the second tab" (org-admin#57) — and the record
        detail screens then adopted it. Two copies of it is one restyle away from
        a portal whose tabs do not match each other.
      */}
      <TabNav
        base="/system-health"
        tabs={SECTIONS}
        current={current}
        label="System health sections"
      />
      {/* Keyed, so switching sections remounts rather than leaving the previous
          section's data on screen under the new heading while its fetch runs. */}
      {current === 'activity' ? <ActivitySection key="activity" /> : <UptimeSection key="uptime" />}
    </div>
  );
}

/**
 * How often the section rechecks, and the default.
 *
 * Ten seconds is offered because Jon asked for it and there are moments — a
 * deploy going out, a service being restarted — when watching it that closely
 * is exactly the point. It is not the DEFAULT, because the common case is a tab
 * somebody left open, and a minute is frequent enough to notice an outage while
 * costing a sixth of the outbound traffic.
 */
const REFRESH_CHOICES = [
  { ms: 10_000, label: 'Every 10 seconds' },
  { ms: 30_000, label: 'Every 30 seconds' },
  { ms: 60_000, label: 'Every minute' },
  { ms: 300_000, label: 'Every 5 minutes' },
  { ms: 900_000, label: 'Every 15 minutes' },
  { ms: 1_800_000, label: 'Every 30 minutes' },
] as const;

const DEFAULT_REFRESH_MS = 60_000;

/**
 * Uptime: check the monitored services now, and keep checking.
 *
 * ── This reverses the "never probe on mount" rule, deliberately ──────
 *
 * `/admin/applications/health` makes real outbound requests, and the portal's
 * long-standing rule was that it only ever ran when somebody pressed a button —
 * a check that fires when a screen opens being "a scanner with a refresh
 * button". Jon's call, 2026-08-27, and it is the right one for THIS screen: a
 * page labelled Uptime whose statuses are from whenever somebody last pressed a
 * button is not a status page, it is a log of button presses.
 *
 * So this checks on arrival and then on an interval the admin picks. The rule
 * still holds everywhere else — the Applications screen keeps its button.
 *
 * ── What that costs, and what is done about it ───────────────────────
 *
 * Two things, and only one of them is handled here.
 *
 * OUTBOUND TRAFFIC is the admin's own choice: they pick the interval, they can
 * see it, and leaving the screen stops it. The default is a minute rather than
 * the shortest option for the tab nobody closed.
 *
 * THE AUDIT LOG is not their choice, and would have been the real damage. Each
 * run writes a `health-probe.run` row kept for a year; at ten seconds that is
 * 8,640 rows a day from one forgotten tab, which would have made this screen the
 * fastest-growing writer of the very table it exists to watch. The server
 * throttles the RECORDING of automatic runs to one per quarter hour while still
 * probing every time — see AUTO_PROBE_RECORD_WINDOW_MS in the auth service. The
 * `auto: true` flag below is what tells it which kind of run this is, and
 * forgetting to send it is what would turn the throttle off.
 *
 * ── Hidden tabs do not poll ──────────────────────────────────────────
 *
 * A backgrounded tab is open but nobody is reading it, so a tick there buys a
 * status nobody sees at the cost of real traffic. Polling pauses while the tab
 * is hidden and fires once immediately on return, which is also the moment the
 * data is most obviously stale.
 */
function UptimeSection() {
  const { state, reload } = useAsync(() => adminApi.uptime());
  const [everyMs, setEveryMs] = useState<number>(DEFAULT_REFRESH_MS);
  const [failed, setFailed] = useState<string | null>(null);
  const [checkedAt, setCheckedAt] = useState<Date | null>(null);

  /**
   * True while a probe is in flight, as a REF rather than state.
   *
   * It guards the timer against stacking: at ten seconds against a service
   * taking twelve to answer, a plain interval would launch a second probe
   * before the first returned and keep launching until the tab fell over.
   * A ref because the guard has to be read and written inside the callback
   * without re-running the effect that owns the timer.
   */
  const inFlight = useRef(false);

  /**
   * `reload` is stable and `everyMs` is not read inside it, so this callback is
   * stable too — which matters because the effect below depends on it and would
   * otherwise tear down and rebuild the timer on every render.
   */
  const runCheck = useCallback(
    async (auto: boolean) => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        await adminApi.checkHealth({ auto });
        setFailed(null);
        setCheckedAt(new Date());
        // Quiet: the table must not blank out and rebuild on every tick.
        reload(true);
      } catch (e) {
        /**
         * Reported, and the table is LEFT ALONE.
         *
         * A failed probe means we could not run the check — not that the
         * services are down. Clearing the rows would turn "we do not know" into
         * "nothing is monitored", which is the collapse this whole codebase
         * keeps arguing against. The statuses stay, visibly stale, with the
         * reason beside them.
         */
        setFailed(e instanceof Error ? e.message : String(e));
      } finally {
        inFlight.current = false;
      }
    },
    [reload],
  );

  /** On arrival. Separate from the timer effect so that changing the interval
   *  does not re-probe — picking "every 30 minutes" should not fire one now. */
  useEffect(() => {
    void runCheck(true);
  }, [runCheck]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      void runCheck(true);
    };
    const timer = window.setInterval(tick, everyMs);

    // Coming back to a backgrounded tab is exactly when the data is most stale,
    // so refresh on return rather than waiting out the rest of the interval.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void runCheck(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [everyMs, runCheck]);

  return (
    <AsyncBoundary state={state}>
      {(d) => (
        <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
          <Panel
            title="Monitored services"
            action={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.75rem' }}>
                {/* The unmonitored count, kept because "everything is healthy"
                    and "we are watching one of three applications" look
                    identical on a screen that only lists what it monitors. */}
                {d.unmonitored > 0 ? (
                  <Badge>{count(d.unmonitored)} not monitored</Badge>
                ) : null}
                <span style={{ fontSize: '.8rem', color: C.muted, whiteSpace: 'nowrap' }}>
                  {checkedAt ? (
                    <>
                      Checked <When iso={checkedAt} />
                    </>
                  ) : d.lastCheckedAt ? (
                    <>
                      Last checked <When iso={d.lastCheckedAt} />
                    </>
                  ) : (
                    'Checking…'
                  )}
                </span>
                <Select
                  size="sm"
                  align="end"
                  label="How often to recheck"
                  value={String(everyMs)}
                  onChange={(v) => setEveryMs(Number(v))}
                  options={REFRESH_CHOICES.map((c) => ({ value: String(c.ms), label: c.label }))}
                />
              </span>
            }
          >
            {d.services.length === 0 ? (
              <EmptyState
                title="No service is being monitored"
                suggestion="An application is monitored once it has a health check URL. Add one on the Applications screen."
              />
            ) : (
              <Table columns={['Service', 'Status', 'Health check']}>
                {d.services.map((svc) => (
                  <tr key={svc.id}>
                    <Td>
                      <span style={{ fontWeight: 600 }}>{svc.name}</span>
                      <span style={{ color: C.muted, marginLeft: '.4rem', fontSize: '.82rem' }}>
                        {svc.slug}
                      </span>
                    </Td>
                    <Td>
                      {/* NOT a green pill, and not a red one either. A service
                          nobody has checked is UNKNOWN, and rendering unknown as
                          healthy is the one thing an uptime screen must never
                          do. With the check now running on arrival this is a
                          brief state rather than the usual one, which makes it
                          more misleading to get wrong, not less. */}
                      {svc.state === null ? (
                        <DotPill tone="muted">Not checked</DotPill>
                      ) : (
                        <HealthState state={svc.state} />
                      )}
                    </Td>
                    <Td mono>{svc.url ?? '—'}</Td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

          {/* The rows above are still the last GOOD result, deliberately. This
              says the refresh is failing, without pretending the services are. */}
          {failed ? <Note tone="danger">Could not run the check: {failed}</Note> : null}
        </div>
      )}
    </AsyncBoundary>
  );
}

/** The activity-log half, which is what this screen was before the split. */
function ActivitySection() {
  const { state } = useAsync(() => adminApi.activityStorage());

  return (
    <AsyncBoundary state={state}>
      {(d) => (
        <div style={{ display: 'grid', gap: '1rem' }} className="rw-fade">
          {/* Renders NOTHING unless a threshold has fired. The top of the
              section is deliberately empty in the ordinary case. */}
          <ThresholdAlert trigger={d.trigger} />
          <ActivityTable table={d.table} retention={d.retention} trigger={d.trigger} purge={d.purge} />
          <PurgeHistory runs={d.purge.runs} thresholdMs={d.trigger.ms} />
        </div>
      )}
    </AsyncBoundary>
  );
}

/**
 * The alert, and it renders nothing at all when nothing is wrong.
 *
 * ── An empty component is the feature (org-admin#56) ─────────────────
 *
 * The obvious shape is three branches returning three coloured boxes, and that
 * is what this replaced. It meant the section always opened with a paragraph
 * explaining a situation that required no action, which is how a reader learns
 * that the box at the top can be skipped. By the time it says something urgent
 * they have been trained for weeks not to look at it.
 *
 * So the only state with a box is the state with something to do. The other two
 * are a pill and a bubble in the panel header, where a status belongs.
 */
function ThresholdAlert({ trigger }: { trigger: ActivityStorage['trigger'] }) {
  if (!trigger.rowsMet && !trigger.msMet) return null;

  return (
    <Note tone='warn'>
      <strong>The activity log has reached a partitioning threshold.</strong>{' '}
      {/* Both, when both. Reporting one while the other is also true would
          understate it, and the two have different remedies. */}
      {[
        trigger.rowsMet ? `it has passed ${count(trigger.rows)} rows` : null,
        trigger.msMet ? `a purge run took longer than ${duration(trigger.ms)}` : null,
      ]
        .filter(Boolean)
        .join(', and ')
        .replace(/^./, (c) => c.toUpperCase())}
      . This is the signal{' '}
      <a href="https://github.com/RidgeWill/auth/issues/51" style={link} target="_blank" rel="noreferrer">
        auth#51
      </a>{' '}
      was deferred against. Nothing is broken and nothing is down, but the work
      is much cheaper now than after the table grows further.
    </Note>
  );
}

/**
 * The status, in the panel header where a status belongs.
 *
 * Three states, and "we do not know yet" is a real one rather than a rounding
 * of healthy. A fresh environment has an unanalysed table and no recorded purge
 * run, and calling that "within thresholds" would be the screen reporting a
 * clean bill of health it has no basis for — the exact failure this feature
 * exists to prevent one level up.
 */
function ThresholdStatus({
  trigger,
  table,
  purge,
}: {
  trigger: ActivityStorage['trigger'];
  table: ActivityStorage['table'];
  purge: ActivityStorage['purge'];
}) {
  const met = trigger.rowsMet || trigger.msMet;
  const unmeasured = table.rows === null && purge.runs.length === 0;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem' }}>
      <DotPill tone={met ? 'warn' : unmeasured ? 'muted' : 'good'}>
        {met ? 'Threshold reached' : unmeasured ? 'Not measured yet' : 'Within thresholds'}
      </DotPill>
      <InfoBubble label='About the partitioning thresholds'>
        <p style={{ margin: 0 }}>
          <strong>Partitioning thresholds.</strong> RidgeWill/auth#51 defers splitting this table
          into one partition per month until it earns it, and this is what watches for that: the
          table passing {count(trigger.rows)} rows, or a purge run taking longer than{' '}
          {duration(trigger.ms)}.
        </p>
        <p style={{ margin: '.6rem 0 0' }}>
          {unmeasured
            ? 'Neither can be evaluated yet. The table has not been analysed and no purge run has been recorded, so this fills in on its own once the retention job has run.'
            : met
              ? 'One of them has been reached, which is what the warning at the top of the section is about.'
              : 'Neither is close. Nothing needs doing, and this pill turns amber if that changes.'}
        </p>
        <p style={{ margin: '.6rem 0 0' }}>
          <a href="https://github.com/RidgeWill/auth/issues/51" style={link} target="_blank" rel="noreferrer">
            Read the reasoning on auth#51
          </a>
        </p>
      </InfoBubble>
    </span>
  );
}

/** The size of the thing, and the windows that are supposed to bound it. */
function ActivityTable({
  table,
  retention,
  trigger,
  purge,
}: {
  table: ActivityStorage['table'];
  retention: ActivityStorage['retention'];
  trigger: ActivityStorage['trigger'];
  purge: ActivityStorage['purge'];
}) {
  return (
    /* The status rides in the header's `action` slot rather than above the
       panel, because it is a fact ABOUT this table and reads as one there. */
    <Panel title="Activity log" action={<ThresholdStatus trigger={trigger} table={table} purge={purge} />}>
      <div style={s.tiles}>
        <Tile
          label="Events"
          value={table.rows === null ? '—' : count(table.rows)}
          /**
           * The exactness is part of the number, not a footnote.
           *
           * Above 64 MB the server stops counting and takes the planner's
           * estimate, because a full scan of this table is the cost this screen
           * exists to watch. An estimate presented without saying so is a figure
           * that ends up quoted in a decision.
           */
          note={table.rows === null ? 'Not analysed yet' : table.rowsExact ? 'Exact count' : 'Planner estimate'}
          accent={C.brand}
        />
        <Tile label="Total size" value={bytes(table.totalBytes)} note="Rows and indexes" accent={C.turquoise} />
        <Tile
          label="Indexes"
          value={bytes(table.indexBytes)}
          /* Worth its own tile: five indexes on a write-heavy table is most of
             what partitioning would shrink, since each partition indexes only
             its own month. */
          note={`${percent(table.indexBytes, table.totalBytes)} of the table`}
          accent={C.violet}
        />
        <Tile
          label="Oldest event"
          value={table.oldestAt ? <When iso={table.oldestAt} /> : '—'}
          note={`Routine events kept ${count(retention.routineDays)} days`}
          accent={C.mutedDot}
        />
      </div>

      <p style={s.footnote}>
        Routine events are deleted after {count(retention.routineDays)} days. Privileged events are
        archived when the account closes and deleted {count(retention.privilegedYears)} years later,
        so the oldest event here can be far older than the routine window.
      </p>
    </Panel>
  );
}

/**
 * The purge, run by run.
 *
 * A history rather than a reading, because one duration says nothing. A purge
 * that takes four seconds having deleted two million rows is a table doing its
 * job; four seconds having deleted none is a table that has stopped fitting in
 * memory, and only the trend separates them.
 */
function PurgeHistory({ runs, thresholdMs }: { runs: ActivityStorage['purge']['runs']; thresholdMs: number }) {
  if (runs.length === 0) {
    return (
      <Panel title="Retention purge">
        <EmptyState
          title="No purge runs recorded yet"
          /**
           * NOT "the purge is not running". The auth service only started
           * writing these down recently, so an empty list is far more likely to
           * mean "no night has passed since" than a stopped job. Saying which
           * keeps somebody from investigating a job that is fine.
           */
          suggestion={
            <>
              The retention job records each run as it finishes, once a night. An empty list here
              usually means no run has happened since the service started recording them, not that
              the job has stopped.
            </>
          }
        />
      </Panel>
    );
  }

  return (
    <Panel title="Retention purge">
      <Table columns={['Run', 'Duration', 'Routine deleted', 'Privileged deleted', '']}>
        {runs.map((r) => (
          <tr key={r.at}>
            <Td>
              <When iso={r.at} />
            </Td>
            <Td>
              {/* The one number auth#51 keys off, so it is the one that changes
                  colour. Amber at the threshold rather than at some fraction of
                  it: a warning tier would be a second number to agree on and
                  this screen already carries the verdict. */}
              <span style={{ ...numeric, color: r.ms >= thresholdMs ? C.warnText : C.text, fontWeight: 600 }}>
                {duration(r.ms)}
              </span>
            </Td>
            <Td>
              <Num value={r.routine} />
            </Td>
            <Td>
              <Num value={r.privileged} />
            </Td>
            <Td>
              {r.ok ? null : <Badge tone="danger">Failed</Badge>}
            </Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function Tile({
  label,
  value,
  note,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  note: string;
  accent: string;
}) {
  return (
    <div style={s.tile}>
      {/* The same accent rail as the Overview's KPIs, for the same reason: a
          tinted card competes with the number on it. */}
      <span style={{ ...s.accent, background: accent }} aria-hidden />
      <span style={s.tileLabel}>{label}</span>
      <span style={{ ...s.tileValue, ...numeric }}>{value}</span>
      <span style={s.tileNote}>{note}</span>
    </div>
  );
}

/** Guarded, because a table with no indexes is a division by zero and this runs
 *  against whatever the database actually reports. */
function percent(part: number, whole: number): string {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

const link = { color: C.brand } as const;

const s = {
  /** A rail of links, not tabs with a box around them. The screen already sits
   *  inside a panelled layout, and a second chrome element competing with the
   *  panel headings makes the page look like two apps. */
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '.75rem',
    padding: '1rem 1.1rem',
  },
  tile: {
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '.15rem',
    padding: '.7rem .85rem .75rem',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  },
  accent: { position: 'absolute' as const, left: 0, top: 0, bottom: 0, width: 3 },
  tileLabel: { fontSize: '.75rem', fontWeight: 600, color: C.muted, letterSpacing: '.01em' },
  tileValue: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1.2 },
  tileNote: { fontSize: '.75rem', color: C.muted },
  footnote: {
    margin: 0,
    padding: '0 1.1rem 1.1rem',
    fontSize: '.82rem',
    lineHeight: 1.55,
    color: C.muted,
  },
  /** The same voice as `footnote`, but sitting on the page rather than inside a
   *  panel — so it carries no panel padding. */
  standaloneNote: {
    margin: 0,
    fontSize: '.82rem',
    lineHeight: 1.55,
    color: C.muted,
  },
};
